import { 
  Calculator, 
  Globe, 
  Shield, 
  Brain,
  Users,
  TrendingUp,
  Clock,
  DollarSign,
  FileText,
  Settings,
  Smartphone,
  BarChart3,
  Lock,
  Zap,
  CheckCircle,
  AlertTriangle,
  Target,
  UserCheck,
  Eye,
  Database,
  Activity,
  Rocket,
  Network
} from 'lucide-react';

export const completePayrollSystemFeatures = {
  keyBenefits: [
    {
      title: 'AI-Powered Payroll Automation',
      description: 'Advanced AI engine automates complex payroll calculations, detects errors before processing, and optimizes compliance across global jurisdictions, reducing processing time by 85%.',
      icon: Brain,
      metrics: '85% time reduction, 99.8% accuracy, Zero manual errors, Real-time validation',
      color: 'from-violet-500 to-purple-600'
    },
    {
      title: 'Global Compliance Management',
      description: 'Comprehensive compliance automation across 180+ countries with real-time regulatory updates, automatic tax calculations, and statutory reporting.',
      icon: Globe,
      metrics: '180+ countries, Real-time updates, 100% compliance rate, Automatic filings',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      title: 'Enterprise Security & Data Protection',
      description: 'Bank-level security with end-to-end encryption, SOC 2 Type II compliance, and comprehensive audit trails protecting sensitive payroll data.',
      icon: Shield,
      metrics: 'SOC 2 certified, AES-256 encryption, GDPR compliant, 99.9% uptime',
      color: 'from-red-500 to-pink-600'
    },
    {
      title: 'Employee Self-Service Excellence',
      description: 'Intuitive employee portal with mobile app access, digital pay stubs, tax documents, benefits enrollment, and direct communication.',
      icon: Smartphone,
      metrics: '95% adoption rate, Mobile-first design, 24/7 access, Multi-language support',
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'Advanced Analytics & Insights',
      description: 'Comprehensive payroll analytics with predictive modeling, cost forecasting, and real-time dashboards to optimize workforce costs.',
      icon: BarChart3,
      metrics: 'Real-time insights, Predictive analytics, 200+ metrics, Custom dashboards',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      title: 'Seamless Integration Ecosystem',
      description: 'Native integrations with 200+ HR, accounting, and time tracking systems plus robust API for custom connections.',
      icon: Settings,
      metrics: '200+ integrations, RESTful API, Real-time sync, Custom webhooks',
      color: 'from-orange-500 to-red-600'
    },

    // Added detailed payroll key benefits
    {
      title: 'Gross-to-Net Calculation Engine',
      description: 'Deterministic and rules-based engine handling complex gross-to-net scenarios including overtime, shift differentials, allowances, deductions, and statutory contributions.',
      icon: Calculator,
      metrics: 'Gross-to-net automation, Complex pay rules, Deterministic engine, Multi-scenario support',
      color: 'from-amber-500 to-yellow-600'
    },
    {
      title: 'Automated Tax Filing & Payments',
      description: 'Automated statutory filings, remittances, and end-to-end tax payment workflows across jurisdictions with complete audit trails.',
      icon: FileText,
      metrics: 'Automatic filings, Remittance automation, Jurisdiction-aware, Audit trails',
      color: 'from-indigo-500 to-blue-600'
    },
    {
      title: 'Multi-Entity & Multi-Currency Payroll',
      description: 'Run consolidated payroll across entities, business units, and countries with real-time FX handling and local compliance.',
      icon: Globe,
      metrics: 'Multi-entity, Multi-currency, Consolidated runs, Local compliance',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      title: 'Payroll Calendars & Cutoffs',
      description: 'Flexible pay calendars with bi-weekly, weekly, semi-monthly, and monthly cycles; automated cutoffs and change windows.',
      icon: Clock,
      metrics: 'Flexible cycles, Automated cutoffs, Change windows, SLA adherence',
      color: 'from-purple-500 to-pink-600'
    },
    {
      title: 'Retro Pay & Adjustments',
      description: 'Automated retro calculations, back-pay, reversals, and adjustment handling with complete auditability.',
      icon: DollarSign,
      metrics: 'Retro automation, Back-pay, Reversals, Full auditability',
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'Off-Cycle & On-Demand Payroll',
      description: 'Support for off-cycle runs, bonuses, corrections, and earned wage access/on-demand pay.',
      icon: Zap,
      metrics: 'Off-cycle runs, Bonus cycles, On-demand pay, Corrections',
      color: 'from-yellow-500 to-orange-600'
    },
    {
      title: 'Leave & Absence Pay Rules',
      description: 'Automated leave accrual, statutory sick pay, holiday pay, maternity/paternity, and custom absence policies.',
      icon: CheckCircle,
      metrics: 'Accrual automation, Statutory pay, Policy engine, Exceptions',
      color: 'from-emerald-500 to-green-600'
    },
    {
      title: 'Union & Collective Agreements Support',
      description: 'Support for union rates, step increases, allowances, and complex collective bargaining agreement rules.',
      icon: Users,
      metrics: 'Union rates, Step increases, Allowances, Agreement rules',
      color: 'from-teal-500 to-cyan-600'
    },
    {
      title: 'GL Posting & Finance Automation',
      description: 'Automatic general ledger mappings, cost centre distributions, accruals, and finance integration with reconciliation.',
      icon: Database,
      metrics: 'GL mappings, Cost centres, Accruals, Reconciliation',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      title: 'Banking & Payment Orchestration',
      description: 'SEPA, BACS, ACH, SWIFT, and payroll card payments with approval workflows and payment status tracking.',
      icon: DollarSign,
      metrics: 'SEPA/BACS/ACH/SWIFT, Approvals, Status tracking, Payroll cards',
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'Year-End Processing & Statutory Documents',
      description: 'Automated year-end processing, P60s/P45s (UK), T4s (CA), W-2s/1099s (US), and jurisdiction-specific documents.',
      icon: FileText,
      metrics: 'Automated year-end, Jurisdiction docs, Employee access, Compliance',
      color: 'from-gray-500 to-slate-600'
    },
    {
      title: 'Data Import, Validation & Exception Handling',
      description: 'Bulk import from HRIS/time systems with schema validation, anomaly detection, and exception queues.',
      icon: Database,
      metrics: 'Schema validation, Anomaly detection, Exception queues, Bulk import',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      title: 'Employee Document Management',
      description: 'Secure storage and delivery of payslips, tax forms, and employment documents with access controls and retention policies.',
      icon: FileText,
      metrics: 'Secure delivery, Access controls, Retention policies, e-Sign ready',
      color: 'from-indigo-500 to-blue-600'
    }
  ],
  
  advancedCapabilities: [
    {
      title: 'Multi-Country Payroll Processing',
      description: 'Unified platform managing payroll across multiple countries with local compliance, currency conversion, and regulatory adherence.',
      icon: Globe,
      metrics: '180+ countries, Multi-currency, Local regulations, Tax optimization',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      title: 'Time & Attendance Integration',
      description: 'Advanced time tracking with biometric integration, overtime calculations, and automated schedule management.',
      icon: Clock,
      metrics: 'Biometric support, Overtime automation, Schedule optimization, Mobile clock-in',
      color: 'from-purple-500 to-pink-600'
    },
    {
      title: 'Benefits Administration Hub',
      description: 'Comprehensive benefits management with enrollment automation, cost tracking, and employee communication.',
      icon: UserCheck,
      metrics: 'Automated enrollment, Cost tracking, Benefits optimization, Communication tools',
      color: 'from-emerald-500 to-green-600'
    },
    {
      title: 'Compliance Automation Engine',
      description: 'Intelligent compliance monitoring with automatic updates, alert systems, and regulatory change management.',
      icon: CheckCircle,
      metrics: 'Automatic updates, Proactive alerts, Change management, Audit preparation',
      color: 'from-yellow-500 to-orange-600'
    },
    {
      title: 'Advanced Reporting Suite',
      description: 'Comprehensive reporting with custom dashboards, scheduled reports, and advanced analytics for strategic decision-making.',
      icon: FileText,
      metrics: 'Custom reports, Scheduled delivery, Advanced analytics, Export options',
      color: 'from-indigo-500 to-blue-600'
    },
    {
      title: 'Payment Processing Center',
      description: 'Multiple payment methods including direct deposit, check printing, payroll cards, and international transfers.',
      icon: DollarSign,
      metrics: 'Multiple methods, International transfers, Instant payments, Payment tracking',
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'Employee Onboarding Automation',
      description: 'Streamlined onboarding with digital forms, background checks, and automated payroll setup for new hires.',
      icon: Users,
      metrics: 'Digital onboarding, Background checks, Automated setup, Compliance tracking',
      color: 'from-teal-500 to-cyan-600'
    },
    {
      title: 'Payroll Forecasting & Planning',
      description: 'Advanced forecasting tools with budget planning, cost analysis, and predictive modeling for strategic planning.',
      icon: TrendingUp,
      metrics: 'Budget planning, Cost analysis, Predictive modeling, Strategic insights',
      color: 'from-violet-500 to-purple-600'
    },
    {
      title: 'Expense Management Integration',
      description: 'Integrated expense management with automated approvals, receipt processing, and reimbursement workflows.',
      icon: Target,
      metrics: 'Automated approvals, Receipt processing, Workflow automation, Mobile submissions',
      color: 'from-rose-500 to-pink-600'
    },
    {
      title: 'Audit Trail & Documentation',
      description: 'Comprehensive audit trails with document management, compliance reporting, and historical data retention.',
      icon: Eye,
      metrics: 'Complete audit trails, Document management, Compliance reports, Data retention',
      color: 'from-gray-500 to-slate-600'
    },
    {
      title: 'Performance-Based Compensation',
      description: 'Advanced compensation management with performance tracking, bonus calculations, and incentive program automation.',
      icon: Calculator,
      metrics: 'Performance tracking, Bonus automation, Incentive programs, Commission tracking',
      color: 'from-amber-500 to-yellow-600'
    },
    {
      title: 'Regulatory Alert System',
      description: 'Proactive regulatory monitoring with real-time alerts, change notifications, and compliance guidance.',
      icon: AlertTriangle,
      metrics: 'Real-time alerts, Change notifications, Compliance guidance, Risk assessment',
      color: 'from-red-500 to-pink-600'
    }
  ],
  
  securityPerformance: [
    {
      icon: Target,
      title: 'Compliance Excellence Framework',
      description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
      metrics: 'Complete regulatory compliance',
      color: 'from-indigo-500 to-purple-600'
    },
    {
      icon: Activity,
      title: 'Security Performance Analytics',
      description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
      metrics: 'Optimized security posture',
      color: 'from-yellow-500 to-amber-600'
    },
    {
      icon: Rocket,
      title: 'Scalable Security Infrastructure',
      description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
      metrics: 'Unlimited security scale',
      color: 'from-emerald-500 to-green-600'
    },
    {
      icon: Shield,
      title: 'Advanced Firewall & DDoS Protection',
      description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
      metrics: '99.99% attack mitigation',
      color: 'from-red-500 to-pink-600'
    },
    {
      icon: Lock,
      title: 'Multi-Factor Authentication Suite',
      description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
      metrics: 'Zero-trust access control',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Eye,
      title: 'Security Information & Event Management',
      description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
      metrics: 'Real-time threat visibility',
      color: 'from-purple-500 to-pink-600'
    },
    {
      icon: Database,
      title: 'Data Loss Prevention & Backup',
      description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
      metrics: 'Zero data loss guarantee',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Network,
      title: 'Network Segmentation & Monitoring',
      description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
      metrics: 'Complete network visibility',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: CheckCircle,
      title: 'Continuous Security Assessment',
      description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
      metrics: 'Proactive security validation',
      color: 'from-cyan-500 to-blue-600'
    }
  ]
};
